/**
 * Shared utility functions for formatting complex values.
 *
 * Extracted from repeated patterns in:
 * - editable-field.tsx
 * - nested-field-accordion.tsx
 * - TableBuilder.tsx
 */

/** Joined values when every member is a scalar (and there is at least one), else null. */
function formatScalarList(items: unknown[], maxLength: number): string | null {
  if (items.length === 0) return null;
  if (!items.every(item => item !== null && item !== undefined && typeof item !== 'object')) {
    return null;
  }
  const joined = items.map(String).join(', ');
  return joined.length > maxLength ? `${joined.slice(0, maxLength)}...` : joined;
}

/**
 * Format a complex value (object/array) for display.
 * Returns a human-readable string representation.
 *
 * For objects, shows the actual JSON content (truncated for readability).
 * For an array of SCALARS, lists the values — a `repeated string` field such as
 * `appeal_required_documents` holds the answer itself, so "6 items" hid what the rep
 * actually dictated and there was no way to read it from the case at all. Arrays of
 * objects keep the count: those are entity lists (`drug`, `procedure`) rendered by
 * their own nested accordions, where a count is the useful summary.
 *
 * @param value - The value to format
 * @param maxLength - Maximum length for JSON strings and joined scalar lists (default: 100)
 * @returns A string representation suitable for display
 */
export function formatComplexValue(value: unknown, maxLength: number = 100): string {
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return (
        formatScalarList(value, maxLength) ?? `${value.length} item${value.length !== 1 ? 's' : ''}`
      );
    }
    // Stringify objects to show actual content (truncated for readability)
    try {
      const str = JSON.stringify(value);
      return str.length > maxLength ? `${str.slice(0, maxLength)}...` : str;
    } catch {
      return '[Object]';
    }
  }
  return String(value ?? '');
}
